import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import Echo from "laravel-echo";
import Pusher from "pusher-js";
import { ConnectionState, EchoContextType, EchoProviderProps } from "./types";

// Garantir que o Pusher está disponível globalmente
declare global {
    interface Window {
        Pusher: typeof Pusher;
    }
}

window.Pusher = Pusher;

// Criar o contexto com valor padrão null
export const EchoContext = createContext<EchoContextType | null>(null);

// Debug mode - apenas log em desenvolvimento
const DEBUG = import.meta.env.DEV;
const log = (...args: any[]) => {
    if (DEBUG) console.log('[WebSocketEcho]', ...args);
};

/**
 * Provider que inicializa e gerencia a conexão WebSocket com Laravel Echo/Reverb
 *
 * @example
 * ```tsx
 * // No app.tsx ou MainProvider.tsx
 * <EchoProvider>
 *   <App />
 * </EchoProvider>
 * ```
 */
export const EchoProvider: React.FC<EchoProviderProps> = ({ children }) => {
    const [connectionState, setConnectionState] = useState<ConnectionState>(ConnectionState.CONNECTING);
    const echoRef = useRef<Echo | null>(null);
    const hasConnectedOnceRef = useRef<boolean>(false);
    const subscriptionsRef = useRef<Map<string, Map<string, (data: any) => void>>>(new Map());

    // Inicializa o Echo ao montar o componente
    useEffect(() => {
        log("========== INICIALIZANDO ECHO ==========");
        log(`VITE_PUSHER_APP_KEY: ${import.meta.env.VITE_PUSHER_APP_KEY}`);
        log(`VITE_PUSHER_HOST: ${import.meta.env.VITE_PUSHER_HOST}`);
        log(`VITE_PUSHER_PORT: ${import.meta.env.VITE_PUSHER_PORT}`);
        log(`VITE_PUSHER_SCHEME: ${import.meta.env.VITE_PUSHER_SCHEME}`);
        log("=========================================");

        // Configurar Laravel Echo
        const echo = new Echo({
            broadcaster: "reverb",
            key: import.meta.env.VITE_PUSHER_APP_KEY,
            wsHost: import.meta.env.VITE_PUSHER_HOST,
            wsPort: import.meta.env.VITE_PUSHER_PORT,
            wssPort: import.meta.env.VITE_PUSHER_PORT,
            forceTLS: (import.meta.env.VITE_PUSHER_SCHEME ?? "https") === "https",
            enabledTransports: ['ws', 'wss'],
        });

        echoRef.current = echo;

        // Gestão de estados de conexão
        echo.connector.pusher.connection.bind("connecting", () => {
            log("========== ESTADO CONEXÃO: CONNECTING ==========");
            log(`Timestamp: ${new Date().toISOString()}`);
            log("===============================================");
            // Apenas mostrar estado connecting se já conectou uma vez (cenário de reconexão)
            if (hasConnectedOnceRef.current) {
                setConnectionState(ConnectionState.CONNECTING);
            }
        });

        echo.connector.pusher.connection.bind("connected", () => {
            log("========== ESTADO CONEXÃO: CONNECTED ==========");
            log(`Timestamp: ${new Date().toISOString()}`);
            log(`Socket ID: ${echo.socketId()}`);
            log("==============================================");
            setConnectionState(ConnectionState.CONNECTED);

            // Resubscrever a todos os canais após reconexão
            if (hasConnectedOnceRef.current) {
                resubscribeAll();
            }

            hasConnectedOnceRef.current = true;
        });

        echo.connector.pusher.connection.bind("disconnected", () => {
            log("========== ESTADO CONEXÃO: DISCONNECTED ==========");
            log(`Timestamp: ${new Date().toISOString()}`);
            log("=================================================");
            setConnectionState(ConnectionState.DISCONNECTED);
        });

        echo.connector.pusher.connection.bind("error", (error: any) => {
            log("========== ERRO DE CONEXÃO ==========");
            log(`Timestamp: ${new Date().toISOString()}`);
            log("Erro:", error);
            log("=====================================");
            setConnectionState(ConnectionState.DISCONNECTED);
        });

        return () => {
            // Limpar Echo ao desmontar
            if (echoRef.current) {
                echoRef.current.connector.pusher.connection.unbind("connecting");
                echoRef.current.connector.pusher.connection.unbind("connected");
                echoRef.current.connector.pusher.connection.unbind("disconnected");
                echoRef.current.connector.pusher.connection.unbind("error");
                echoRef.current.disconnect();
                echoRef.current = null;
            }
            hasConnectedOnceRef.current = false;
            subscriptionsRef.current.clear();
        };
    }, []);

    // Função para resubscrever a todos os canais após reconexão
    const resubscribeAll = useCallback(() => {
        if (!echoRef.current) return;

        log("Resubscrevendo a todos os canais...");
        subscriptionsRef.current.forEach((events, channelName) => {
            const channel = echoRef.current!.private(channelName);
            events.forEach((callback, eventName) => {
                channel.listen(`.${eventName}`, callback);
            });
        });
    }, []);

    // Função para subscrever a um canal e evento
    const subscribe = useCallback((channelName: string, eventName: string, callback: (data: any) => void): () => void => {
        if (!echoRef.current) {
            log(`[WARN] Echo não inicializado, não é possível subscrever a ${channelName} -> ${eventName}`);
            return () => {};
        }

        log("========== SUBSCRIBE ==========");
        log(`Canal: ${channelName}`);
        log(`Evento: ${eventName}`);
        log(`Full event name: .${eventName}`);
        log(`Timestamp: ${new Date().toISOString()}`);
        log("================================");

        // Obter ou criar o canal
        const channel = echoRef.current.private(channelName);

        // Guardar a subscrição para reconexão
        if (!subscriptionsRef.current.has(channelName)) {
            subscriptionsRef.current.set(channelName, new Map());
        }
        const channelEvents = subscriptionsRef.current.get(channelName)!;
        channelEvents.set(eventName, callback);

        // Escutar o evento
        channel.listen(`.${eventName}`, callback);

        log(`[OK] Subscrito com sucesso a ${channelName} -> .${eventName}`);

        // Retornar função de unsubscribe
        return () => {
            unsubscribe(channelName, eventName);
        };
    }, []);

    // Função para remover subscrição de um canal e evento
    const unsubscribe = useCallback((channelName: string, eventName: string) => {
        if (!echoRef.current) return;

        log(`Unsubscribe: ${channelName} -> ${eventName}`);

        // Obter o canal e parar de escutar
        const channel = echoRef.current.private(channelName);
        channel.stopListening(`.${eventName}`);

        // Remover do tracking de subscrições
        const channelEvents = subscriptionsRef.current.get(channelName);
        if (channelEvents) {
            channelEvents.delete(eventName);
            if (channelEvents.size === 0) {
                subscriptionsRef.current.delete(channelName);
            }
        }
    }, []);

    return (
        <EchoContext.Provider value={{
            echo: echoRef.current,
            connectionState,
            subscribe,
            unsubscribe
        }}>
            {children}
        </EchoContext.Provider>
    );
};

/**
 * Hook para aceder ao contexto Echo
 *
 * @throws Error se usado fora do EchoProvider
 *
 * @example
 * ```tsx
 * const { echo, connectionState, subscribe } = useEcho();
 *
 * useEffect(() => {
 *   const unsubscribe = subscribe("mesas.1", "MesaAtualizada", (data) => {
 *     console.log("Mesa atualizada:", data);
 *   });
 *   return unsubscribe;
 * }, [subscribe]);
 * ```
 */
export const useEcho = (): EchoContextType => {
    const context = useContext(EchoContext);
    if (!context) {
        throw new Error("useEcho deve ser usado dentro de um EchoProvider");
    }
    return context;
};
